import React, { useState } from "react";
import { Send, CheckCircle2, Phone, Mail, Clock, MapPin, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";

const AREAS = ["Civil", "Commercial", "Constitutional", "Property & Real Estate", "Family", "Service", "Consumer", "Regulatory", "Other"];
const PHONES = [
  { display: "+91 94440 14561", tel: "+919444014561" },
  { display: "+91 88845 43456", tel: "+918884543456" },
  { display: "+91 93630 14561", tel: "+919363014561" },
  { display: "+91 78128 98967", tel: "+917812898967" },
];
const EMAILS = ["mksikdar@rediffmail.com", "kirtisikdar11@gmail.com"];

const INITIAL = { name: "", email: "", phone: "", area: "Civil", message: "" };


const WHATSAPP_NUMBER = "918884543456"; // +91 88845 43456
const NOTIFY_EMAIL = "kirtisikdar11@gmail.com";

function buildEnquiryMessage(f) {
  return [
    "Hello Sikdar & Sikdar — Advocates,",
    "",
    "I would like to request a consultation.",
    "",
    `Name: ${f.name}`,
    `Email: ${f.email}`,
    `Phone: ${f.phone}`,
    `Area of Law: ${f.area}`,
    "",
    "Matter:",
    f.message,
    "",
    "— Sent from your website",
  ].join("\n");
}

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [lastEnquiry, setLastEnquiry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    // Build outbound message BEFORE the network call so we can open a
    // new tab synchronously within the submit gesture (avoids pop-up blockers).
    const message = buildEnquiryMessage(form);
    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    const waWindow = window.open(waUrl, "_blank", "noopener,noreferrer");

    try {
      await api.post("/enquiries", form);
      setLastEnquiry(form);
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Enquiry received. WhatsApp opened for delivery.");
    } catch (err) {
      // If the API failed, close the WhatsApp tab we opened so we don't create confusion
      try { waWindow && waWindow.close(); } catch (_e) { /* ignore */ }
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section"
      style={{ backgroundColor: "#0A1128", color: "#F9F6F0" }}
    >
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <span className="ornament text-xs" style={{ letterSpacing: "0.28em", textTransform: "uppercase", color: "#C5A059" }}>
              Request a Consultation
            </span>
            <h2 className="font-serif-display mt-6 text-4xl md:text-5xl" style={{ color: "#F9F6F0", fontWeight: 500, lineHeight: 1.1 }}>
              Begin a measured
              <br />
              <span style={{ fontStyle: "italic", color: "#C5A059" }}>conversation.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg max-w-md" style={{ color: "rgba(249,246,240,0.78)", lineHeight: 1.7 }}>
              Share a brief outline of your matter. We respond personally within one
              working day to schedule a consultation, in person or by call.
            </p>

            <div className="mt-10 space-y-6 text-sm" data-testid="contact-direct">
              <div>
                <div className="text-[10px] uppercase mb-3" style={{ color: "#C5A059", letterSpacing: "0.24em" }}>
                  Direct Lines
                </div>
                <ul className="space-y-2">
                  {PHONES.map((p) => (
                    <li key={p.tel}>
                      <a
                        href={`tel:${p.tel}`}
                        className="flex items-center gap-3 hover:underline"
                        style={{ color: "#F9F6F0" }}
                        data-testid={`contact-phone-${p.tel}`}
                      >
                        <Phone size={14} strokeWidth={1.5} style={{ color: "#C5A059" }} />
                        {p.display}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex items-start gap-3" style={{ color: "rgba(249,246,240,0.85)" }}>
                <Mail size={14} strokeWidth={1.5} className="mt-1 shrink-0" style={{ color: "#C5A059" }} />
                <div className="space-y-1">
                  {EMAILS.map((em) => (
                    <div key={em}>
                      <a href={`mailto:${em}`} className="hover:underline break-all" style={{ color: "#F9F6F0" }} data-testid={`contact-email-${em}`}>
                        {em}
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-3" style={{ color: "rgba(249,246,240,0.85)" }}>
                <MapPin size={14} strokeWidth={1.5} className="mt-1 shrink-0" style={{ color: "#C5A059" }} />
                <span data-testid="contact-address" style={{ color: "#F9F6F0" }}>
                  No. 31, Palavedu Road,<br/>
                  Mittanamalli, IAF Avadi,<br/>
                  Chennai – 600 055
                </span>
              </div>

              <div className="flex items-start gap-3" style={{ color: "rgba(249,246,240,0.85)" }}>
                <Clock size={14} strokeWidth={1.5} className="mt-1 shrink-0" style={{ color: "#C5A059" }} />
                <span>Monday – Saturday · 10:00 AM – 7:00 PM<br/>Sunday by appointment</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="p-10 md:p-14 h-full flex flex-col items-start justify-center"
                style={{ border: "1px solid rgba(249,246,240,0.18)" }}
              >
                <CheckCircle2 size={36} strokeWidth={1.4} style={{ color: "#C5A059" }} />
                <h3 className="font-serif-display text-3xl mt-6" style={{ color: "#F9F6F0", fontWeight: 500 }}>
                  Thank you. Your enquiry has been received.
                </h3>
                <p className="mt-4 text-base" style={{ color: "rgba(249,246,240,0.78)" }}>
                  We have opened WhatsApp with your enquiry so you can send it directly to the
                  Chamber. If it did not open, use one of the options below.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(buildEnquiryMessage(lastEnquiry || {}))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                    style={{ backgroundColor: "#128C7E", borderColor: "#128C7E", color: "#F9F6F0" }}
                    data-testid="contact-success-whatsapp"
                  >
                    <MessageCircle size={14} strokeWidth={2} /> Send on WhatsApp
                  </a>
                  <a
                    href={`mailto:${NOTIFY_EMAIL}?subject=${encodeURIComponent("Consultation Request — Sikdar & Sikdar")}&body=${encodeURIComponent(buildEnquiryMessage(lastEnquiry || {}))}`}
                    className="btn-secondary"
                    style={{ color: "#F9F6F0", borderColor: "rgba(249,246,240,0.5)" }}
                    data-testid="contact-success-email"
                  >
                    <Mail size={14} strokeWidth={2} /> Send by Email
                  </a>
                </div>

                <button
                  type="button"
                  className="btn-ghost-light mt-10"
                  onClick={() => { setSubmitted(false); setLastEnquiry(null); }}
                  data-testid="contact-success-reset"
                >
                  Submit Another Enquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="p-8 md:p-12 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8"
                style={{ border: "1px solid rgba(249,246,240,0.18)", backgroundColor: "rgba(255,255,255,0.02)" }}
                data-testid="contact-form"
              >
                <Field label="Full Name" required>
                  <input
                    className="field-input"
                    style={{ color: "#F9F6F0", borderBottomColor: "rgba(249,246,240,0.25)" }}
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    minLength={2}
                    data-testid="contact-name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    className="field-input"
                    style={{ color: "#F9F6F0", borderBottomColor: "rgba(249,246,240,0.25)" }}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    data-testid="contact-email"
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    className="field-input"
                    style={{ color: "#F9F6F0", borderBottomColor: "rgba(249,246,240,0.25)" }}
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    required
                    minLength={6}
                    data-testid="contact-phone"
                  />
                </Field>
                <Field label="Area of Law" required>
                  <select
                    className="field-select"
                    style={{ color: "#F9F6F0", borderBottomColor: "rgba(249,246,240,0.25)" }}
                    name="area"
                    value={form.area}
                    onChange={onChange}
                    required
                    data-testid="contact-area"
                  >
                    {AREAS.map((a) => (
                      <option key={a} value={a} style={{ color: "#0A1128" }}>
                        {a}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Briefly describe your matter" required>
                    <textarea
                      className="field-textarea"
                      style={{ color: "#F9F6F0", borderBottomColor: "rgba(249,246,240,0.25)" }}
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      minLength={5}
                      rows={5}
                      data-testid="contact-message"
                    />
                  </Field>
                </div>

                {error && (
                  <div className="sm:col-span-2 text-sm" style={{ color: "#FCA5A5" }} data-testid="contact-error">
                    {error}
                  </div>
                )}

                <div className="sm:col-span-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-4"
                  style={{ borderTop: "1px solid rgba(249,246,240,0.12)" }}
                >
                  <p className="text-xs sm:flex-1 sm:pr-4" style={{ color: "rgba(249,246,240,0.55)" }}>
                    Submission does not constitute an attorney-client relationship.
                  </p>
                  <button
                    type="submit"
                    className="btn-primary-sm whitespace-nowrap self-start sm:self-auto shrink-0"
                    style={{ backgroundColor: "#C5A059", borderColor: "#C5A059", color: "#0A1128" }}
                    disabled={submitting}
                    data-testid="contact-submit"
                  >
                    {submitting ? "Sending…" : "Send via WhatsApp"}
                    <MessageCircle size={14} strokeWidth={2} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="field-label" style={{ color: "rgba(249,246,240,0.7)" }}>
        {label} {required && <span style={{ color: "#C5A059" }}>*</span>}
      </span>
      {children}
    </label>
  );
}
